/* eslint-disable no-console */
import { calcularImporteNetoIntermediario } from "."; // ajustá la ruta si es necesario
import { AGENCIAS_DIRECTAS, SIN_COMISION_2025 } from "../constants/permisos";
import { calcularComisionEdificiosTotal, resolverFechaFactura } from "./comisionEdificiosUtil";

// Fecha de corte para ID 32
const EXECUTIVE_CUTOFF_DATE = new Date("2025-06-01");

// Usa resolverFechaFactura centralizada y retorna Date
const resolverFechaComision = (op) => new Date(resolverFechaFactura(op));

export const calculateComission = {
  /**
   * Comisión de agencia (intermediario).
   * Preferir pasar opportunity con importe ya en moneda local (oppEnLocal).
   * tipoCambio es legacy — solo lo usa VentasFacturasEjecutivo.
   */
  calculateAgencyCommission: (opportunity, clientes, intermediarios, tipoCambio) => {
    let comisionIntermediarioPorcentaje = 0;
    if (!opportunity.isBTL) {
      const clienteAsociado = clientes[opportunity.client_id];
      // Override de intermediario por venta (acuerdos de volumen, ej: OMNET): si está poblado,
      // se resuelve desde ese intermediario en vez de cliente.agencia.
      const overrideId = parseInt(opportunity.intermediario_override_id, 10);
      const intermediarioIdResuelto =
        opportunity.intermediario_override_id != null && !isNaN(overrideId) ? overrideId : clienteAsociado?.agencia;
      if (intermediarioIdResuelto) {
        const intermediarioAsociado = intermediarios[intermediarioIdResuelto];
        if (intermediarioAsociado) {
          const fechaFactura = resolverFechaComision(opportunity);
          comisionIntermediarioPorcentaje =
            intermediarioAsociado.comision_2 &&
            intermediarioAsociado.comision_2_fechainicio &&
            intermediarioAsociado.comision_2_fechafin &&
            fechaFactura >= new Date(intermediarioAsociado.comision_2_fechainicio) &&
            fechaFactura <= new Date(intermediarioAsociado.comision_2_fechafin)
              ? intermediarioAsociado.comision_2 || 0
              : intermediarioAsociado.comision || 0;
          if (AGENCIAS_DIRECTAS.includes(intermediarioIdResuelto)) comisionIntermediarioPorcentaje = 0;
        }
      }
    } else {
      comisionIntermediarioPorcentaje = opportunity.comision || 0;
      if (AGENCIAS_DIRECTAS.includes(opportunity.intermediarioId || opportunity.intermediario))
        comisionIntermediarioPorcentaje = 0;
    }
    const importeBruto = parseFloat(opportunity.importe) || 0;
    const comision = importeBruto * (comisionIntermediarioPorcentaje / 100);
    // Legacy: si el caller pasa tipoCambio y la opp está en USD, convierte el resultado
    if (tipoCambio && opportunity.moneda_dolar) return comision * tipoCambio;
    return comision;
  },

  /**
   * Comisión comercial total (para ER y cálculos).
   * Preferir pasar opportunity con importe ya en moneda local (oppEnLocal).
   * tipoCambio es legacy — solo lo usa VentasFacturasEjecutivo.
   */
  calculateSalesCommission: (opportunity, clientes, intermediarios, edificios, tipoCambio, snapMap = null) => {
    const agencyCommission = calculateComission.calculateAgencyCommission(
      opportunity,
      clientes,
      intermediarios,
      tipoCambio
    );
    const netoDespuesAgencias = calcularImporteNetoIntermediario(
      opportunity,
      Object.values(clientes),
      Object.values(intermediarios),
      opportunity.pais
    );
    const buildingCommission = calcularComisionEdificiosTotal(opportunity, edificios, netoDespuesAgencias, snapMap);
    const importeBruto = parseFloat(opportunity.importe) || 0;
    const netoFinal = importeBruto - agencyCommission - buildingCommission;
    if (netoFinal <= 0) return 0;

    // Parseo de mes_cierre "MM-YYYY"
    let monthCierre = null;
    let yearCierre = null;
    if (opportunity.mes_cierre) {
      const [mesStr, anoStr] = opportunity.mes_cierre.split("-");
      monthCierre = parseInt(mesStr, 10) - 1; // 01 → 0
      yearCierre = parseInt(anoStr, 10);
    }

    const fechaFactura = resolverFechaComision(opportunity);
    const ejecutivoId = parseInt(opportunity.ejecutivo);

    // Exclusión ID 32 (aplica a ambos países)
    if (ejecutivoId === 32 && fechaFactura >= EXECUTIVE_CUTOFF_DATE) {
      return 0;
    }

    let totalComision = 0;

    if (opportunity.pais === "Perú") {
      // Priscilla (ID 7): siempre 1.25% universal
      totalComision += netoFinal * 0.0125;

      // ID 6: 3.75% si mes_cierre <= junio 2025 (dejó la empresa)
      if (yearCierre === 2025 && monthCierre <= 5) {
        totalComision += netoFinal * 0.0375;
      }

      // Karin (ID 306): 4% cuentas bajo su gestión, 1% resto — desde julio 2025
      if ((yearCierre === 2025 && monthCierre >= 6) || yearCierre > 2025) {
        const clienteAsociado = clientes[opportunity.client_id];
        const esGestionKarin = clienteAsociado && String(clienteAsociado.ejecutivo) === "306";
        const percentage306 = esGestionKarin ? 4 : 1;
        totalComision += netoFinal * (percentage306 / 100);
      }
    } else if (opportunity.pais === "Chile") {
      // Año de facturación: facturafecha real, o año planificado (ano) para pendientes
      const yearFactura = opportunity.facturafecha
        ? fechaFactura.getFullYear()
        : parseInt(opportunity.ano, 10) || fechaFactura.getFullYear();

      if (yearFactura <= 2025) {
        // Lógica 2025: escala descendente por trimestre
        if (!opportunity.ejecutivo) return 0;
        if (SIN_COMISION_2025.includes(ejecutivoId)) return 0;

        const monthFactura = fechaFactura.getMonth();
        let percentage = 0;
        if (yearFactura === 2025) {
          if (monthFactura <= 2) percentage = 8;
          else if (monthFactura <= 5) percentage = 7;
          else if (monthFactura <= 8) percentage = 6;
          else percentage = 5;
        }
        totalComision += netoFinal * (percentage / 100);
      } else {
        // 2026+: Nicolás (904) cobra solo en ventas CERRADAS (mes_cierre) en 2026+
        if (yearCierre && yearCierre >= 2026) {
          const clienteAsociado = clientes[opportunity.client_id];
          const esGestionNicolas = clienteAsociado && String(clienteAsociado.ejecutivo) === "904";
          const percentage = esGestionNicolas ? 4 : 1;
          totalComision += netoFinal * (percentage / 100);
        }
        // Ventas cerradas en 2025 facturadas en 2026+: $0 comisión comercial
      }
    }

    return totalComision;
  },

  /**
   * Desglose de comisión comercial para modal (array de objetos).
   * Preferir pasar opportunity con importe ya en moneda local (oppEnLocal).
   * tipoCambio es legacy — solo lo usa VentasFacturasEjecutivo.
   */
  getSalesCommissionBreakdown: (opportunity, clientes, intermediarios, edificios, tipoCambio, snapMap = null) => {
    const agencyCommission = calculateComission.calculateAgencyCommission(
      opportunity,
      clientes,
      intermediarios,
      tipoCambio
    );
    const netoDespuesAgencias = calcularImporteNetoIntermediario(
      opportunity,
      Object.values(clientes),
      Object.values(intermediarios),
      opportunity.pais
    );
    const buildingCommission = calcularComisionEdificiosTotal(opportunity, edificios, netoDespuesAgencias, snapMap);
    const importeBruto = parseFloat(opportunity.importe) || 0;
    const netoFinal = importeBruto - agencyCommission - buildingCommission;
    if (netoFinal <= 0) return [];

    let monthCierre = null;
    let yearCierre = null;
    if (opportunity.mes_cierre) {
      const [mesStr, anoStr] = opportunity.mes_cierre.split("-");
      monthCierre = parseInt(mesStr, 10) - 1;
      yearCierre = parseInt(anoStr, 10);
    }

    const fechaFactura = resolverFechaComision(opportunity);
    const ejecutivoId = parseInt(opportunity.ejecutivo);

    // Exclusión ID 32
    if (ejecutivoId === 32 && fechaFactura >= EXECUTIVE_CUTOFF_DATE) return [];

    const desglose = [];

    if (opportunity.pais === "Perú") {
      // Priscilla (ID 7) siempre
      desglose.push({ ejecutivo: 7, percentage: 1.25, comision: netoFinal * 0.0125 });

      // ID 6 si aplica
      if (yearCierre === 2025 && monthCierre <= 5) {
        desglose.push({ ejecutivo: 6, percentage: 3.75, comision: netoFinal * 0.0375 });
      }

      // Karin (ID 306): 4% gestión / 1% resto — desde julio 2025
      if ((yearCierre === 2025 && monthCierre >= 6) || yearCierre > 2025) {
        const clienteAsociado = clientes[opportunity.client_id];
        const esGestionKarin = clienteAsociado && String(clienteAsociado.ejecutivo) === "306";
        const percentage306 = esGestionKarin ? 4 : 1;
        desglose.push({ ejecutivo: 306, percentage: percentage306, comision: netoFinal * (percentage306 / 100) });
      }
    } else if (opportunity.pais === "Chile") {
      // Año de facturación: facturafecha real, o año planificado (ano) para pendientes
      const yearFactura = opportunity.facturafecha
        ? fechaFactura.getFullYear()
        : parseInt(opportunity.ano, 10) || fechaFactura.getFullYear();

      if (yearFactura <= 2025) {
        // Lógica 2025
        if (!opportunity.ejecutivo) return [];
        if (SIN_COMISION_2025.includes(ejecutivoId)) return [];

        const monthFactura = fechaFactura.getMonth();
        let percentage = 0;
        if (yearFactura === 2025) {
          if (monthFactura <= 2) percentage = 8;
          else if (monthFactura <= 5) percentage = 7;
          else if (monthFactura <= 8) percentage = 6;
          else percentage = 5;
        }
        if (percentage > 0) {
          desglose.push({ ejecutivo: ejecutivoId, percentage, comision: netoFinal * (percentage / 100) });
        }
      } else {
        // 2026+: Nicolás (904) cobra solo en ventas CERRADAS (mes_cierre) en 2026+
        if (yearCierre && yearCierre >= 2026) {
          const clienteAsociado = clientes[opportunity.client_id];
          const esGestionNicolas = clienteAsociado && String(clienteAsociado.ejecutivo) === "904";
          const percentage = esGestionNicolas ? 4 : 1;
          desglose.push({ ejecutivo: 904, percentage, comision: netoFinal * (percentage / 100) });
        }
        // Ventas cerradas en 2025 facturadas en 2026+: $0 comisión comercial
      }
    }

    // Si el ejecutivo de la oportunidad no está en el desglose, agregarlo con 0%
    // (ej: Sergio cerró la venta pero dejó la empresa → aparece con $0)
    if (ejecutivoId && !desglose.some((d) => d.ejecutivo === ejecutivoId)) {
      desglose.unshift({ ejecutivo: ejecutivoId, percentage: 0, comision: 0 });
    }

    return desglose;
  },
};
